import { UzAlert, UzButton, UzDialog, UzSkeleton } from '@utazzo/ui';
import { Undo2 } from 'lucide-react';
import React from 'react';

import { dateTimeLabel, money } from '../booking-format';
import { useRefundQuote } from '../use-travel-bookings';

import { KeyValueList } from './KeyValueList';

/**
 * What opens when an admin clicks the "cancelled - refund the guest"
 * notification: the cancellation in numbers, on top of the booking.
 *
 * Guest name, what they paid, what the supplier was to be paid, what it
 * charged and credits back, our own taxes and fees, and what is left to
 * refund. Two ways out: "See details" closes it to the booking page
 * underneath, "Refund now" opens the refund dialog with these same figures.
 * The numbers are read live from the refund quote, so a notification opened
 * a day late still shows the truth rather than what the payload said then.
 */
export const CancellationNoticeDialog = ({
  open,
  onOpenChange,
  bookingId,
  reference,
  cancellation,
  onRefund,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  reference: string;
  cancellation: {
    status: 'PENDING' | 'PROCESSED' | 'REJECTED';
    resolvedAt: string | null;
    creditNoteNo: string | null;
    rejectionReason: string | null;
  } | null;
  onRefund: () => void;
}): React.JSX.Element => {
  const quote = useRefundQuote(open ? bookingId : null);
  const q = quote.data;
  const c = q?.currencyCode ?? 'INR';
  const settled = cancellation?.status === 'PROCESSED';
  const canRefund = Boolean(q?.refundable) && BigInt(q?.maxRefundPaise ?? '0') > 0n;

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        cancellation?.status === 'REJECTED'
          ? `Cancellation refused - ${reference}`
          : settled
            ? `Cancelled by the supplier - ${reference}`
            : `Cancellation pending - ${reference}`
      }
      description={
        cancellation?.status === 'REJECTED'
          ? 'The supplier refused the cancellation. The booking is still live and nothing is owed.'
          : settled
            ? `Settled ${dateTimeLabel(cancellation?.resolvedAt)}. The supplier has returned its share to our account; the guest's refund is your decision.`
            : 'The supplier has not settled this cancellation yet. The figures below will firm up when it does.'
      }
      size="md"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <UzButton
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            See details
          </UzButton>
          {canRefund && (
            <UzButton
              className="w-full sm:w-auto"
              onClick={() => {
                onOpenChange(false);
                onRefund();
              }}
              leftIcon={<Undo2 className="h-4 w-4" />}
            >
              Refund now
            </UzButton>
          )}
        </div>
      }
    >
      {quote.isLoading || !q ? (
        <UzSkeleton className="h-40 w-full" />
      ) : (
        <div className="flex flex-col gap-3">
          {cancellation?.rejectionReason && (
            <UzAlert
              tone="warning"
              title="Supplier said"
              description={cancellation.rejectionReason}
            />
          )}
          <KeyValueList
            items={[
              { term: 'Guest', value: q.guestName || '-' },
              { term: 'Guest paid', value: money(q.guestPaidPaise, c), strong: true },
              {
                term: 'We booked it for',
                value: `${money(q.supplierNetMinor, q.supplierCurrency)} ${q.supplierCurrency}`,
              },
              {
                term: 'Supplier charge',
                value:
                  q.supplierChargePaise !== null
                    ? money(q.supplierChargePaise, c)
                    : 'awaiting supplier',
              },
              {
                term: 'Supplier credits us',
                value:
                  q.supplierCreditPaise !== null
                    ? money(q.supplierCreditPaise, c)
                    : 'awaiting supplier',
              },
              { term: 'Our convenience fee', value: money(q.convenienceFeePaise, c) },
              { term: 'GST collected', value: money(q.gstPaise, c) },
              { term: 'Already refunded', value: money(q.alreadyRefundedPaise, c) },
              { term: 'Still refundable', value: money(q.maxRefundPaise, c), strong: true },
              ...(q.suggestedRefundPaise !== null
                ? [{ term: 'Suggested refund', value: money(q.suggestedRefundPaise, c) }]
                : []),
              ...(cancellation?.creditNoteNo
                ? [{ term: 'Credit note', value: cancellation.creditNoteNo }]
                : []),
            ]}
          />
          {!canRefund && q.blockers.length > 0 && (
            <p className="text-xs text-zinc-500">{q.blockers.join(' ')}</p>
          )}
        </div>
      )}
    </UzDialog>
  );
};
